import { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { MoonFilterChip } from "@louise/moon-ui";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import {
  BiologyDeepCarousel,
  buildEggFreezingBiologySlides,
} from "./EggFreezingBiologyDeepCarouselCard";
import {
  buildPreCycleBiologySlides,
  type PreCycleBiologyVariant,
} from "./EmbryoFreezingUnderstandStep";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import { buildSpermFreezingBiologySlides } from "./SpermFreezingUnderstandStep";
import { TimelineSubtaskCard } from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";

export type ExplorerViewerSexAtBirth = "AFAB" | "AMAB" | "unknown";

type ExplorerBiologyOptionId =
  "egg-freezing" | "embryo-freezing" | "iui" | "ivf" | "sperm-freezing";

type ExplorerBiologyOption = {
  id: ExplorerBiologyOptionId;
  label: string;
};

function buildAvailableBiologyOptions(
  viewerSexAtBirth: ExplorerViewerSexAtBirth,
  t: TranslationFunction,
): ExplorerBiologyOption[] {
  // Sex at birth is the only availability signal Explorer has before the
  // user shares partner, donor, or source details. Those inputs can narrow
  // or expand this list later without changing the card or its carousels.
  const optionIds: ExplorerBiologyOptionId[] =
    viewerSexAtBirth === "AMAB"
      ? ["sperm-freezing", "embryo-freezing", "ivf"]
      : viewerSexAtBirth === "AFAB"
        ? ["egg-freezing", "embryo-freezing", "iui", "ivf"]
        : ["egg-freezing", "embryo-freezing", "iui", "ivf", "sperm-freezing"];

  const labels: Record<ExplorerBiologyOptionId, string> = {
    "egg-freezing": t(
      "homepage.guided_precycle.learn_explorer.biology_option_egg_freezing",
    ),
    "embryo-freezing": t(
      "homepage.guided_precycle.learn_explorer.biology_option_embryo_freezing",
    ),
    iui: t("homepage.guided_precycle.learn_explorer.biology_option_iui"),
    ivf: t("homepage.guided_precycle.learn_explorer.biology_option_ivf"),
    "sperm-freezing": t(
      "homepage.guided_precycle.learn_explorer.biology_option_sperm_freezing",
    ),
  };

  return optionIds.map((id) => ({ id, label: labels[id] }));
}

function SelectedBiologyCarousel({
  selectedId,
  userAge,
  onAction,
  t,
}: {
  selectedId: ExplorerBiologyOptionId;
  userAge?: number;
  onAction: (action: PlanAction) => void;
  t: TranslationFunction;
}) {
  if (selectedId === "egg-freezing") {
    return (
      <BiologyDeepCarousel
        userAge={userAge}
        onAction={onAction}
        slides={buildEggFreezingBiologySlides(t)}
        showEstimateBridge={false}
        illustrationVariant="organic-explanatory"
      />
    );
  }

  if (selectedId === "sperm-freezing") {
    return (
      <BiologyDeepCarousel
        userAge={userAge}
        onAction={onAction}
        slides={buildSpermFreezingBiologySlides(t)}
        showEstimateBridge={false}
        illustrationVariant="organic-explanatory"
      />
    );
  }

  const variant: PreCycleBiologyVariant = selectedId;
  return (
    <BiologyDeepCarousel
      userAge={userAge}
      onAction={onAction}
      slides={buildPreCycleBiologySlides(variant, t)}
      showEstimateBridge={false}
      illustrationVariant="organic-explanatory"
    />
  );
}

export function ExplorerBiologyOptionsCard({
  userAge,
  viewerSexAtBirth,
  onAction,
}: {
  userAge?: number;
  viewerSexAtBirth: ExplorerViewerSexAtBirth;
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const options = useMemo(
    () => buildAvailableBiologyOptions(viewerSexAtBirth, t),
    [t, viewerSexAtBirth],
  );
  const [selectedId, setSelectedId] = useState<ExplorerBiologyOptionId>(
    options[0]?.id ?? "egg-freezing",
  );

  useEffect(() => {
    if (!options.some((option) => option.id === selectedId)) {
      setSelectedId(options[0]?.id ?? "egg-freezing");
    }
  }, [options, selectedId]);

  return (
    <StepperSection
      id="precycle-explorer.options"
      stepLabel={t(
        "homepage.guided_precycle.learn_explorer.section_options_step_label",
      )}
      showRail={false}
    >
      <TimelineSubtaskCard
        id="precycle-explorer.options.biology-options"
        title={t(
          "homepage.guided_precycle.learn_explorer.biology_options_title",
        )}
        subtitle={t(
          "homepage.guided_precycle.learn_explorer.biology_options_subtitle",
        )}
        titleIcon={<OrganicMiniIcon kind="process" size={34} />}
        collapsedPreviewHeight={68}
      >
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 14,
          }}
        >
          {options.map((option) => (
            <MoonFilterChip
              key={option.id}
              label={option.label}
              selected={selectedId === option.id}
              onPress={() => setSelectedId(option.id)}
              tone="glass"
              showCheck={false}
              testID={`explorer-biology-option-${option.id}`}
            />
          ))}
        </View>

        <View>
          <SelectedBiologyCarousel
            selectedId={selectedId}
            userAge={userAge}
            onAction={onAction}
            t={t}
          />
        </View>
      </TimelineSubtaskCard>
    </StepperSection>
  );
}
